import { EMOJI_GROUP, EmojiGroupId, EmojiItem } from "./types";

type EmojiEntry = readonly [emoji: string, name: string, ...keywords: string[]];

const toItems = (
  group: EmojiGroupId,
  entries: readonly EmojiEntry[],
): EmojiItem[] =>
  entries.map(([emoji, name, ...keywords]) => ({
    emoji,
    name,
    keywords,
    group,
  }));

// Unicode "Smileys & Emotion" (RGI, no skin-tone variants).
const SMILEYS: readonly EmojiEntry[] = [
  ["😀", "grinning face", "grinning", "smile", "happy"],
  ["😃", "grinning face with big eyes", "smiley", "happy", "joy"],
  ["😄", "grinning face with smiling eyes", "smile", "happy", "laugh"],
  ["😁", "beaming face with smiling eyes", "grin"],
  ["😆", "grinning squinting face", "laughing", "happy", "haha"],
  ["😅", "grinning face with sweat", "sweat smile", "hot"],
  ["🤣", "rolling on the floor laughing", "rofl", "lol", "laughing"],
  ["😂", "face with tears of joy", "joy", "tears", "lol"],
  ["🙂", "slightly smiling face", "smile"],
  ["🙃", "upside-down face", "upside down"],
  ["😉", "winking face", "wink", "flirt"],
  ["😊", "smiling face with smiling eyes", "blush", "proud"],
  ["😇", "smiling face with halo", "innocent", "angel"],
  ["🥰", "smiling face with hearts", "love", "crush"],
  ["😍", "smiling face with heart-eyes", "love", "heart eyes"],
  ["🤩", "star-struck", "eyes", "star"],
  ["😘", "face blowing a kiss", "kiss", "flirt"],
  ["😗", "kissing face", "kiss"],
  ["☺️", "smiling face", "relaxed", "blush"],
  ["😚", "kissing face with closed eyes", "kiss"],
  ["😙", "kissing face with smiling eyes", "kiss"],
  ["🥲", "smiling face with tear", "touched", "grateful"],
  ["😋", "face savoring food", "yum", "tongue"],
  ["😛", "face with tongue", "tongue"],
  ["😜", "winking face with tongue", "wink", "prank", "silly"],
  ["🤪", "zany face", "goofy", "wacky"],
  ["😝", "squinting face with tongue", "prank", "tongue"],
  ["🤑", "money-mouth face", "rich", "money"],
  ["🤗", "smiling face with open hands", "hug", "hugs"],
  ["🤭", "face with hand over mouth", "quiet", "whoops"],
  ["🤫", "shushing face", "silence", "quiet"],
  ["🤔", "thinking face", "thinking", "hmm"],
  ["🤐", "zipper-mouth face", "silence", "hush"],
  ["🤨", "face with raised eyebrow", "suspicious", "skeptical"],
  ["😐", "neutral face", "meh", "neutral"],
  ["😑", "expressionless face", "blank"],
  ["😶", "face without mouth", "mute", "silence"],
  ["😶‍🌫️", "face in clouds", "absent"],
  ["😏", "smirking face", "smirk", "smug"],
  ["😒", "unamused face", "meh", "unamused"],
  ["🙄", "face with rolling eyes", "eyeroll"],
  ["😬", "grimacing face", "grimace", "awkward"],
  ["😮‍💨", "face exhaling", "relief", "sigh"],
  ["🤥", "lying face", "liar"],
  ["😌", "relieved face", "relief", "whew"],
  ["😔", "pensive face", "sad", "pensive"],
  ["😪", "sleepy face", "tired", "sleepy"],
  ["🤤", "drooling face", "drool"],
  ["😴", "sleeping face", "zzz", "sleep"],
  ["😷", "face with medical mask", "sick", "ill", "mask"],
  ["🤒", "face with thermometer", "sick"],
  ["🤕", "face with head-bandage", "hurt", "injured"],
  ["🤢", "nauseated face", "sick", "disgusted"],
  ["🤮", "face vomiting", "sick", "vomit"],
  ["🤧", "sneezing face", "achoo", "sick"],
  ["🥵", "hot face", "heat", "sweating"],
  ["🥶", "cold face", "freezing", "ice"],
  ["🥴", "woozy face", "groggy"],
  ["😵", "face with crossed-out eyes", "dizzy", "knocked out"],
  ["😵‍💫", "face with spiral eyes", "dizzy"],
  ["🤯", "exploding head", "mind blown"],
  ["🤠", "cowboy hat face", "cowboy"],
  ["🥳", "partying face", "party", "celebration", "birthday"],
  ["🥸", "disguised face", "disguise"],
  ["😎", "smiling face with sunglasses", "cool", "sunglasses"],
  ["🤓", "nerd face", "geek", "glasses"],
  ["🧐", "face with monocle", "monocle"],
  ["😕", "confused face", "confused"],
  ["😟", "worried face", "nervous", "worried"],
  ["🙁", "slightly frowning face", "frown", "sad"],
  ["☹️", "frowning face", "frown", "sad"],
  ["😮", "face with open mouth", "surprise", "wow"],
  ["😯", "hushed face", "silence", "speechless"],
  ["😲", "astonished face", "amazed", "gasp"],
  ["😳", "flushed face", "flushed", "embarrassed"],
  ["🥺", "pleading face", "please", "puppy"],
  ["😦", "frowning face with open mouth", "frown"],
  ["😧", "anguished face", "stunned"],
  ["😨", "fearful face", "scared", "shocked"],
  ["😰", "anxious face with sweat", "nervous", "sweat"],
  ["😥", "sad but relieved face", "disappointed", "phew"],
  ["😢", "crying face", "sad", "tear", "cry"],
  ["😭", "loudly crying face", "sad", "cry", "sob"],
  ["😱", "face screaming in fear", "scream", "shocked"],
  ["😖", "confounded face", "confounded"],
  ["😣", "persevering face", "struggling"],
  ["😞", "disappointed face", "sad", "disappointed"],
  ["😓", "downcast face with sweat", "sweat"],
  ["😩", "weary face", "tired", "weary"],
  ["😫", "tired face", "tired", "upset"],
  ["🥱", "yawning face", "yawn", "tired"],
  ["😤", "face with steam from nose", "triumph", "smug"],
  ["😡", "pouting face", "angry", "mad", "rage"],
  ["😠", "angry face", "angry", "mad"],
  ["🤬", "face with symbols on mouth", "swearing", "cursing"],
  ["😈", "smiling face with horns", "devil", "evil"],
  ["👿", "angry face with horns", "devil", "angry"],
  ["💀", "skull", "dead", "danger"],
  ["☠️", "skull and crossbones", "danger", "pirate"],
  ["💩", "pile of poo", "poop"],
  ["🤡", "clown face", "clown"],
  ["👹", "ogre", "monster"],
  ["👺", "goblin", "monster"],
  ["👻", "ghost", "halloween"],
  ["👽", "alien", "ufo"],
  ["👾", "alien monster", "game", "space invader"],
  ["🤖", "robot", "robot"],
  ["😺", "grinning cat", "cat", "happy"],
  ["😸", "grinning cat with smiling eyes", "cat", "smile"],
  ["😹", "cat with tears of joy", "cat", "joy"],
  ["😻", "smiling cat with heart-eyes", "cat", "love"],
  ["😼", "cat with wry smile", "cat", "smirk"],
  ["😽", "kissing cat", "cat", "kiss"],
  ["🙀", "weary cat", "cat", "shocked"],
  ["😿", "crying cat", "cat", "sad", "cry"],
  ["😾", "pouting cat", "cat", "angry"],
  ["🙈", "see-no-evil monkey", "monkey", "ignore"],
  ["🙉", "hear-no-evil monkey", "monkey", "deaf"],
  ["🙊", "speak-no-evil monkey", "monkey", "mute"],
  ["💋", "kiss mark", "kiss", "lips"],
  ["💌", "love letter", "email", "love"],
  ["💘", "heart with arrow", "love", "cupid"],
  ["💝", "heart with ribbon", "love", "gift"],
  ["💖", "sparkling heart", "love", "sparkle"],
  ["💗", "growing heart", "love"],
  ["💓", "beating heart", "love", "heartbeat"],
  ["💞", "revolving hearts", "love"],
  ["💕", "two hearts", "love"],
  ["💟", "heart decoration", "love"],
  ["❣️", "heart exclamation", "love"],
  ["💔", "broken heart", "heartbreak", "sad"],
  ["❤️‍🔥", "heart on fire", "love", "passion"],
  ["❤️‍🩹", "mending heart", "healing"],
  ["❤️", "red heart", "love", "heart"],
  ["🧡", "orange heart", "love"],
  ["💛", "yellow heart", "love"],
  ["💚", "green heart", "love"],
  ["💙", "blue heart", "love"],
  ["💜", "purple heart", "love"],
  ["🤎", "brown heart", "love"],
  ["🖤", "black heart", "love"],
  ["🤍", "white heart", "love"],
  ["💯", "hundred points", "100", "perfect", "score"],
  ["💢", "anger symbol", "angry"],
  ["💥", "collision", "boom", "explode"],
  ["💫", "dizzy", "star"],
  ["💦", "sweat droplets", "water", "sweat"],
  ["💨", "dashing away", "wind", "fast"],
  ["🕳️", "hole", "hole"],
  ["💣", "bomb", "boom"],
  ["💬", "speech balloon", "comment", "message"],
  ["👁️‍🗨️", "eye in speech bubble", "witness"],
  ["🗨️", "left speech bubble", "comment"],
  ["🗯️", "right anger bubble", "angry"],
  ["💭", "thought balloon", "thinking"],
  ["💤", "zzz", "sleeping", "sleep"],
];

// Unicode "People & Body" hand gestures only. Skin-tone variants are omitted
// from the grid; workplace-inappropriate glyphs are excluded.
const GESTURES: readonly EmojiEntry[] = [
  ["👋", "waving hand", "wave", "hello", "goodbye"],
  ["🤚", "raised back of hand", "backhand"],
  ["🖐️", "hand with fingers splayed", "hand"],
  ["✋", "raised hand", "high five", "stop"],
  ["🖖", "vulcan salute", "spock", "vulcan"],
  ["👌", "OK hand", "ok", "okay"],
  ["🤌", "pinched fingers", "pinch"],
  ["🤏", "pinching hand", "small", "tiny"],
  ["✌️", "victory hand", "peace", "victory"],
  ["🤞", "crossed fingers", "luck", "hope"],
  ["🤟", "love-you gesture", "love"],
  ["🤘", "sign of the horns", "rock", "metal"],
  ["🤙", "call me hand", "call", "phone"],
  ["👈", "backhand index pointing left", "left", "point"],
  ["👉", "backhand index pointing right", "right", "point"],
  ["👆", "backhand index pointing up", "up", "point"],
  ["👇", "backhand index pointing down", "down", "point"],
  ["☝️", "index pointing up", "up", "point"],
  ["👍", "thumbs up", "+1", "thumbsup", "approve", "ok"],
  ["👎", "thumbs down", "-1", "thumbsdown", "disapprove"],
  ["✊", "raised fist", "fist", "power"],
  ["👊", "oncoming fist", "punch", "fist"],
  ["🤛", "left-facing fist", "fist"],
  ["🤜", "right-facing fist", "fist"],
  ["👏", "clapping hands", "clap", "applause", "praise"],
  ["🙌", "raising hands", "hooray", "celebration"],
  ["👐", "open hands", "hands"],
  ["🤲", "palms up together", "please"],
  ["🤝", "handshake", "deal", "agree"],
  ["🙏", "folded hands", "please", "thanks", "pray"],
  ["💪", "flexed biceps", "muscle", "strong"],
];

export const EMOJIS: EmojiItem[] = [
  ...toItems(EMOJI_GROUP.SMILEYS, SMILEYS),
  ...toItems(EMOJI_GROUP.GESTURES, GESTURES),
];
